import { useEffect, useMemo, useRef, useState } from "react";

/**
 * useABVariant — devuelve una variante estable para el usuario (persistida
 * en sessionStorage) y dispara automáticamente el evento `impression` al
 * backend la primera vez que se monta con esa variante.
 *
 * Uso:
 *   const { variant, trackConversion } = useABVariant("hero_headline", ["A","B"]);
 *   return <h1>{variant === "A" ? headlineA : headlineB}</h1>;
 *   // en el click del CTA:
 *   trackConversion("cta_click");
 *
 * Weights opcional: pasa un array de mismos length que variants.
 * En URL ?ab_variant=<experimentId>:<variantName> se puede forzar variante.
 */
export function useABVariant(experimentId, variants, options = {}) {
  const { weights, storageKey = "kelo_ab" } = options;
  const trackedRef = useRef(false);

  const variant = useMemo(() => {
    if (!experimentId || !Array.isArray(variants) || variants.length === 0) return null;

    // 1) URL override
    try {
      const params = new URLSearchParams(window.location.search);
      const forced = params.get("ab_variant");
      if (forced && forced.startsWith(`${experimentId}:`)) {
        const wanted = forced.split(":", 2)[1];
        if (variants.includes(wanted)) return wanted;
      }
    } catch {
      /* ignore */
    }

    // 2) Sticky sessionStorage
    try {
      const raw = sessionStorage.getItem(storageKey);
      const map = raw ? JSON.parse(raw) : {};
      if (map[experimentId] && variants.includes(map[experimentId])) {
        return map[experimentId];
      }
      // 3) Weighted random pick
      const pick = weightedPick(variants, weights);
      map[experimentId] = pick;
      sessionStorage.setItem(storageKey, JSON.stringify(map));
      return pick;
    } catch {
      return variants[0];
    }
  }, [experimentId, variants, weights, storageKey]);

  // Fire impression exactly once per (session, experiment, variant).
  useEffect(() => {
    if (!experimentId || !variant) return;
    if (trackedRef.current) return;
    trackedRef.current = true;
    try {
      const key = `kelo_ab_impr_${experimentId}_${variant}`;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
      const base = process.env.REACT_APP_BACKEND_URL;
      fetch(`${base}/api/ab/impression`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          experiment_id: experimentId,
          variant,
          session: getSession(),
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* silently ignore analytics failure */
    }
  }, [experimentId, variant]);

  const trackConversion = (goal = "default") => {
    if (!experimentId || !variant) return;
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      fetch(`${base}/api/ab/conversion`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          experiment_id: experimentId,
          variant,
          session: getSession(),
          goal,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* silently ignore */
    }
  };

  return { variant, trackConversion };
}

function getSession() {
  try {
    let s = sessionStorage.getItem("kelo_ab_session");
    if (!s) {
      s =
        (crypto?.randomUUID?.() || String(Date.now())) +
        "-" +
        Math.random().toString(36).slice(2, 8);
      sessionStorage.setItem("kelo_ab_session", s);
    }
    return s;
  } catch {
    return "anon";
  }
}

function weightedPick(variants, weights) {
  const w = Array.isArray(weights) && weights.length === variants.length
    ? weights
    : variants.map(() => 1);
  const total = w.reduce((a, b) => a + b, 0);
  let r = Math.random() * total;
  for (let i = 0; i < variants.length; i += 1) {
    r -= w[i];
    if (r <= 0) return variants[i];
  }
  return variants[variants.length - 1];
}

export default useABVariant;
